import type {
  CoachingSession,
  CoachingSessionStage,
  CoachSessionTranscriptResponse,
  SessionChallenge,
  SessionPrep,
  SessionSmartAction,
} from '@doneverse/shared'
import { api } from '../api/index.js'

/**
 * Doer-safe projection of a coaching session for the AI-coach widget's
 * session-close screen (ClickUp 86d36nq9u) and in-session context panel
 * (ClickUp 86d36nq1h). Mirrors the API's `GET /v1/coaching-sessions/:id`
 * response: the session summary fields the doer may see, the SMART actions
 * they committed to, their own pre-session prep, and the challenges they
 * stated in-session — never the internal risk_score or escalations.
 */
export interface DoerCoachingSession {
  session: Pick<
    CoachingSession,
    'id' | 'status' | 'current_stage' | 'summary' | 'closed_at' | 'loyalty_delta'
  >
  smart_actions: SessionSmartAction[]
  prep: SessionPrep | null
  challenges: Pick<SessionChallenge, 'id' | 'description' | 'impact_score' | 'action_item' | 'due_date'>[]
}

/**
 * One row in the doer's session-history list (ClickUp 86d36nqc4). A flat,
 * doer-safe summary — the per-session SMART actions / challenges / prep load
 * from `fetchById` when the doer opens a card, so the list stays a cheap query.
 * Mirrors the API's `toDoerSafeSessionSummary`; never carries risk_score or
 * escalations.
 */
export interface DoerSessionSummary {
  id: string
  status: CoachingSession['status']
  current_stage: CoachingSessionStage | null
  summary: string | null
  started_at: string | null
  closed_at: string | null
  loyalty_delta: number | null
}

export const coachingSessionService = {
  /**
   * The acting doer's own past sessions for the active workspace, newest first.
   * Mirrors `GET /v1/coaching-sessions`; the workspace is resolved server-side
   * from the `x-doneverse-workspace-id` header the API client sends.
   */
  list: async (): Promise<DoerSessionSummary[]> => {
    const { sessions } = await api
      .get('/v1/coaching-sessions')
      .json<{ sessions: DoerSessionSummary[]; current: DoerSessionSummary | null }>()
    return sessions
  },

  /**
   * The doer's CURRENT session for the single `/ai-coaching` surface, resolved
   * server-side: their open session, this week's just-closed one (§9.2 weekly
   * cadence), or null when they're due to prep a new one. The dashboard renders
   * by this — no client-side "which session am I in" tracking. Mirrors the
   * `current` field on `GET /v1/coaching-sessions`.
   */
  getCurrent: async (): Promise<DoerSessionSummary | null> => {
    const { current } = await api
      .get('/v1/coaching-sessions')
      .json<{ sessions: DoerSessionSummary[]; current: DoerSessionSummary | null }>()
    return current
  },

  /** The doer's own session + its SMART actions, by id. */
  fetchById: async (id: string): Promise<DoerCoachingSession> => {
    return api.get(`/v1/coaching-sessions/${id}`).json<DoerCoachingSession>()
  },

  /**
   * The doer's own prior turns for a session, flattened to chat messages so the
   * AI-coach widget re-renders the thread on resume instead of a blank one
   * (ClickUp 86d36nqch). A session spans several ElevenLabs conversations across
   * drop + resume; `conversations` carries each one's start/end so the widget
   * can mark the pauses. Mirrors `GET /v1/coaching-sessions/:id/transcript`;
   * empty until a dropped conversation's post-call webhook lands. Doer-safe —
   * it's the doer's own conversation.
   */
  getTranscript: async (id: string): Promise<CoachSessionTranscriptResponse> => {
    return api.get(`/v1/coaching-sessions/${id}/transcript`).json<CoachSessionTranscriptResponse>()
  },

  /**
   * Persist the session's current stage — the widget's optimistic, forward-only
   * write fired as the agent advances through the `ui_stage_*` markers, so the
   * stopped-at stage survives a refresh/leave. Mirrors
   * `PATCH /v1/coaching-sessions/:id/stage`; returns the stage now stored.
   */
  updateStage: async (
    id: string,
    stage: CoachingSessionStage,
  ): Promise<{ current_stage: CoachingSessionStage | null }> => {
    return api
      .patch({ stage }, `/v1/coaching-sessions/${id}/stage`)
      .json<{ current_stage: CoachingSessionStage | null }>()
  },
}
